import React, { useState, useEffect } from 'react';
import { isObjEmpty } from '../../../../helpers';
import { Link } from 'react-router-dom';
import { validatePhoneNumber } from '../../../../helpers/phone_number_helper';
import { createPreSignup } from '../../../../api/pre_signups';
import { failToast } from '../../../ui_kit/utilities/fail_toast';

import EmailInput from '../../../ui_kit/inputs/email/index';
import PhoneNumberInput from '../../../ui_kit/inputs/phone_number/index';
import RequestButton from '../../../ui_kit/utilities/request_button';
import ErrorMessage from '../../../ui_kit/utilities/error_message';
import AgreeToTerms from '../../../ui_kit/utilities/agree_to_terms';
import LocationInput from '../../../ui_kit/inputs/location';
import PlatformModal from '../../../ui_kit/modals/platform_modal';

import styles from './index.module.css';

const PreSignupForm = ({}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [condition, setCondition] = useState('');
  const [anythingElse, setAnythingElse] = useState('');
  const [showConditionLabel, setShowConditionLabel] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [isDisabled, setIsDisabled] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [location, setLocation] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createAndRedirect = async () => {
    // Prevent multiple rapid submissions
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await createPreSignup({
        pre_signup: {
          first_name: firstName,
          last_name: lastName,
          email: email,
          phone: phone,
          condition: condition,
          anything_else: anythingElse,
          location: location,
        },
      });

      if (response.success) {
        setShowModal(true);
      } else {
        failToast(response.errors.join(', '));
        setIsSubmitting(false); // Re-enable on error
      }
    } catch (error) {
      failToast('Something went wrong. Please try again.');
      setIsSubmitting(false); // Re-enable on error
    }
  };

  const handleModalClose = () => {
    window.location.reload();
  };

  const validatePhoneOnBlur = () => {
    if (!validatePhoneNumber(phone)) {
      setPhoneError('Invalid phone number');
    } else {
      setPhoneError('');
    }
  };

  useEffect(() => {
    if (
      firstName &&
      lastName &&
      email &&
      phone &&
      !phoneError &&
      !isObjEmpty(location) &&
      condition &&
      acceptedTerms &&
      !isSubmitting
    ) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [firstName, lastName, email, phone, phoneError, location, condition, acceptedTerms, isSubmitting]);

  return (
    <div className={`${styles.pre_signup_container} container`}>
      <div className='row h-100'>
        <div className='col-md-3 no-mobile'></div>
        <div className='registrations-container'>
          <h5 className={'text-center marg-top-20 marg-bottom-10'}>{'Want to learn more?'}</h5>
          <p className={'text-center login-title marg-bottom-20'}>
            {
              'Our team will reach out to help match you with a mentor who can provide support and guidance. Fill out the form below to get started and learn more about how our program can support you!'
            }
          </p>

          {showModal && (
            <PlatformModal
              title="Submission Received! We'll Contact You Shortly"
              setModal={setShowModal}
              onClick={handleModalClose}
              cancelCta=''
              shouldApplyClickAway={false}
              content={
                <>
                  <p>
                    We've received your information and a member of our team will contact you by the next business day.
                  </p>
                  <br />
                  <p>
                    If you'd like to contact us directly, please email us at{' '}
                    <a style={{ color: '#419DA5' }} href='mailto:info@patientpartner.com'>
                      info@patientpartner.com
                    </a>{' '}
                    or call us at{' '}
                    <a style={{ color: '#419DA5' }} href='tel:+1-619-268-1112'>
                      +1-619-268-1112
                    </a>
                    .
                  </p>
                </>
              }
            />
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            <div className={`material-input ${styles.first_name}`}>
              <input
                className='autofocus fs-safe'
                type='text'
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                required
              />
              <span className='highlight'></span>
              <span className='bar'></span>
              <label>First Name</label>
            </div>
            <div className={`material-input ${styles.last_name}`}>
              <input
                className='fs-safe'
                type='text'
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                required
              />
              <span className='highlight'></span>
              <span className='bar'></span>
              <label>Last Name</label>
            </div>
          </div>

          <EmailInput value={email} setValue={setEmail} name='' />

          <PhoneNumberInput phone={phone} setPhone={setPhone} onBlur={validatePhoneOnBlur} required={true} />
          <ErrorMessage error={phoneError} />

          <LocationInput
            value={location}
            setValue={setLocation}
            placeholder={'Enter city or zip code'}
            style={{ maxWidth: '100%', width: '100%' }}
            showSmallLabel
            countryCode={''}
          />

          <div className={`material-input`}>
            <input
              type='text'
              name={'condition'}
              value={condition}
              onChange={(event) => {
                setCondition(event.target.value);
              }}
              placeholder={showConditionLabel ? null : 'What condition and/or treatment are you seeking support for?'}
              required
              onFocus={() => setShowConditionLabel(true)}
              onBlur={() => setShowConditionLabel(false)}
              className={styles.condition_input}
            />
            <label
              className={`${styles.condition_label} ${showConditionLabel || condition != '' ? styles.in_final_position : ''}`}
            >
              Condition/Treatment
            </label>
          </div>

          <div className={`material-input`}>
            <input
              type='text'
              name={'anything_else'}
              value={anythingElse}
              onChange={(event) => {
                setAnythingElse(event.target.value);
              }}
              required
            />
            <label className={styles.anything_else_label}>Anything else you'd like to add</label>
          </div>

          <AgreeToTerms acceptedTerms={acceptedTerms} setAcceptedTerms={setAcceptedTerms} />

          <RequestButton
            request={createAndRedirect}
            recaptcha={true}
            disabled={isDisabled}
            classnames={'marg-top-10 submit'}
            style={{ width: '100%' }}
            id={'pre_signup_button'}
            cta={isSubmitting ? 'Submitting...' : 'Submit'}
          />

          <p className={'marg-top-30 marg-bottom-30 text-center'} style={{ fontSize: '16px' }}>
            Already have an account?{' '}
            <Link style={{ fontSize: '16px' }} to={'member/login'} className='inline-link'>
              Log in
            </Link>
          </p>

          <p className={'marg-top-30 marg-bottom-30'} style={{ fontSize: '16px' }}>
            PatientPartner is not intended for use in medical emergencies. If you are experiencing a medical emergency,
            call 911 immediately or go to the nearest emergency department.
          </p>
        </div>
      </div>
      <div className='col-md-3 no-mobile'></div>
    </div>
  );
};

export default PreSignupForm;
